/**
 * Client for the Reeve upload flow (see backend/README.md "Uploads").
 *
 * The flow is document-coupled and three-step:
 *   1. createDocument(...)          -> the Document entity and its id
 *   2. requestUpload(...)           -> a short-lived, write-only upload ticket
 *      putFileToTicket(ticket, f)   -> PUT the bytes DIRECTLY to storage (not our API)
 *   3. completeUpload(uploadId, documentId) -> server-side smoke checks; on success the
 *      file is promoted to the document (a new immutable version)
 *
 * The ticket URL is a CNAMEd storage URL (e.g. files.reeve.hr) and `ticket.headers` are
 * opaque ticket data the PUT must carry — callers should not care that they happen to be
 * Azure-specific.
 *
 * Auth: token attachment is centralised in auth.ts. Each call resolves the live
 * Clerk session bearer via getAuthToken() (null until signed in, in which case no
 * Authorization header is sent and the request 401s). The API is deliberately
 * tenant-less: the backend resolves the tenant from the token server-side, so
 * nothing here ever sees a tenant id.
 */

import { getAuthToken } from './auth';
import { ensureDocx } from './convert';

export type UploadPurposeDocument = {
  companyId: string;
  name: string;
  employeeId?: string;
  caseId?: string;
  catalogId?: string;
  summary?: string;
};

export interface UploadTicket {
  uploadId: string;
  uploadUrl: string;
  method: string;
  headers: Record<string, string>;
  urlExpiresAt: string;
}

export type UploadStatus = 'awaitingUpload' | 'verified' | 'rejected';

export interface UploadCompletion {
  uploadId: string;
  documentId: string;
  status: UploadStatus;
  sizeBytes: number | null;
  rejectionReason: string | null;
}

export interface DocumentDto {
  id: string;
  companyId: string;
  employeeId: string | null;
  caseId: string | null;
  catalogId: string | null;
  name: string;
  summary: string;
  category: string;
  status: 'ok' | 'needsUpdate' | 'missing';
  file: {
    originalName: string;
    contentType: string;
    sizeBytes: number;
    version: number;
    uploadedAt: string;
  } | null;
  inFlightUploads: { uploadedAt: string }[];
  createdAt: string;
  lastUpdated: string;
}

async function apiJson<T>(apiBase: string, path: string, init?: RequestInit): Promise<T> {
  // getAuthToken() is the source of truth (the Clerk session token, or null when
  // not signed in — then no Authorization header and the request 401s).
  const token = await getAuthToken();
  const response = await fetch(`${apiBase}${path}`, {
    ...init,
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      'Content-Type': 'application/json',
      ...init?.headers,
    },
  });
  if (!response.ok) {
    const body = await response.text().catch(() => '');
    throw new Error(`${init?.method ?? 'GET'} ${path} failed (${response.status}): ${body}`);
  }
  return (await response.json()) as T;
}

/** Creates the Document entity an upload will complete against. */
export function createDocument(
  apiBase: string,
  request: UploadPurposeDocument,
): Promise<DocumentDto> {
  return apiJson<DocumentDto>(apiBase, '/api/documents', {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

export function getDocument(apiBase: string, documentId: string): Promise<DocumentDto> {
  return apiJson<DocumentDto>(apiBase, `/api/documents/${documentId}`);
}

/** Step 1 of the upload: mint the handshake row + the direct-to-storage ticket. */
export function requestUpload(
  apiBase: string,
  request: { fileName: string; contentType?: string },
): Promise<UploadTicket> {
  return apiJson<UploadTicket>(apiBase, '/api/uploads', {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

/**
 * Step 2: PUT the bytes straight to storage using the ticket. A plain fetch is all a
 * ticketed upload needs — the ticket's headers carry everything storage requires.
 */
export async function putFileToTicket(ticket: UploadTicket, file: Blob): Promise<void> {
  const response = await fetch(ticket.uploadUrl, {
    method: ticket.method,
    headers: ticket.headers,
    body: file,
  });
  if (!response.ok) {
    throw new Error(`Upload PUT failed (${response.status})`);
  }
}

/** Step 3: couple the upload to its document and run the server-side checks. */
export function completeUpload(
  apiBase: string,
  uploadId: string,
  documentId: string,
): Promise<UploadCompletion> {
  return apiJson<UploadCompletion>(apiBase, `/api/uploads/${uploadId}/complete`, {
    method: 'POST',
    body: JSON.stringify({ documentId }),
  });
}

/**
 * The whole flow against an existing document: ticket -> direct PUT -> complete.
 * Throws on transport errors; returns the completion verdict (which may be `rejected` —
 * check `status` and surface `rejectionReason` to the user).
 */
export async function uploadDocumentFile(
  apiBase: string,
  documentId: string,
  file: File,
): Promise<UploadCompletion> {
  // Normalise to .docx BEFORE requesting the ticket. reeve stores only .docx, and the backend smoke check
  // keys off the extension we register at requestUpload time — so it must match the bytes we PUT. Files
  // already .docx pass through unconverted (this also covers the editor's Accept-save path).
  const { blob, fileName, contentType } = await ensureDocx(file);
  const ticket = await requestUpload(apiBase, { fileName, contentType });
  await putFileToTicket(ticket, blob);
  return completeUpload(apiBase, ticket.uploadId, documentId);
}
